/**
 * Cadastros — o padrão 02: lista com painel de cadastro à direita.
 *
 * Seis abas, uma tela. As entidades estão descritas em `esquemas.ts` e o painel
 * é o `PainelDeCadastro`, que é a mesma peça que o diagrama e o lançamento vão
 * hospedar. Aqui ela mora numa gaveta de 400 px.
 *
 * A aba de **conectores** é a única com uma regra própria: o conector pertence a
 * um componente, e a lista mostra só os do componente escolhido. Sem isso a aba
 * abre com centenas de linhas soltas que ninguém consegue relacionar.
 */

import { useEffect, useMemo, useState } from "react"
import { useParams, useSearchParams } from "react-router-dom"
import {
  usarChicotes, usarComponentes, usarConectores, usarCores, usarRestricoes,
} from "@/api/catalogos"
// O de circuitos mora à parte: ele busca no servidor, porque são milhares por
// projeto e não cabem em memória como os outros cinco.
import { usarCircuitos } from "@/api/circuitos"
import {
  usarExcluir, usarLista, usarSalvar, type ItemDeCadastro,
} from "@/api/cadastros"
import { POR_PAGINA } from "@/api/query"
import { obter } from "@/api/cliente"
import { ErroDaApi } from "@/api/erros"
import { LinhaDeAviso, Tabela, type Coluna } from "@/kit/Tabela"
import { BarraDeEstado } from "@/kit/BarraDeEstado"
import { Multi } from "@/kit/Multi"
import { ABAS, ESQUEMAS, type EntidadeCadastro } from "./esquemas"
import { PainelDeCadastro, type ResultadoDeSalvar } from "./PainelDeCadastro"
import { Transferir } from "./Transferir"

export function Cadastros() {
  const { projetoId } = useParams()
  const id = Number(projetoId)

  const [aba, setAba] = useState<EntidadeCadastro>("componentes")
  const [busca, setBusca] = useState("")
  const [aplicada, setAplicada] = useState("")
  const [pagina, setPagina] = useState(1)
  const [selecionado, setSelecionado] = useState<ItemDeCadastro | null>(null)
  const [incluindo, setIncluindo] = useState(false)
  // Só conectores: qual componente está filtrando a lista.
  const [componenteDoConector, setComponenteDoConector] = useState<number[]>([])

  // `?aba=conectores&id=120697` vem do painel do pinout gráfico. Abre a aba e
  // seleciona o registro; sem isso o botão levaria à tela certa e à lista
  // inteira, e a pessoa procuraria de novo o que já tinha na mão.
  const [parametros, setParametros] = useSearchParams()
  useEffect(() => {
    const pedida = parametros.get("aba") as EntidadeCadastro | null
    const registro = Number(parametros.get("id"))
    if (!pedida || !(pedida in ESQUEMAS)) return
    setAba(pedida)
    if (registro > 0) {
      obter<ItemDeCadastro>(`${ESQUEMAS[pedida].rota}/${registro}`, {})
        .then(setSelecionado)
        .catch(() => { /* registro apagado; a aba abre mesmo assim */ })
    }
    parametros.delete("aba"); parametros.delete("id")
    setParametros(parametros, { replace: true })
  }, [parametros, setParametros])

  const esquema = ESQUEMAS[aba]
  const componentes = usarComponentes(id)

  /**
   * Os catálogos das seis abas, para o destino da transferência.
   *
   * Todos já vêm de uma chamada por projeto por sessão (regra 1), então tê-los
   * aqui não custa ida nova ao banco — é o mesmo dado que os filtros usam.
   */
  const conectoresCat = usarConectores(id)
  const circuitosCat = usarCircuitos(id)
  const coresCat = usarCores(id)
  const chicotesCat = usarChicotes(id)
  const restricoesCat = usarRestricoes(id)
  const catalogoDaAba = {
    componentes, conectores: conectoresCat, circuitos: circuitosCat,
    cores: coresCat, chicotes: chicotesCat, restricoes: restricoesCat,
  }[aba]

  const [transferindo, setTransferindo] = useState(false)

  // Trocar de aba zera tudo: o que estava selecionado não existe na aba nova.
  useEffect(() => {
    setSelecionado(null); setIncluindo(false); setTransferindo(false)
    setBusca(""); setAplicada(""); setPagina(1)
  }, [aba])

  const ehConector = aba === "conectores"
  const filtro = useMemo(
    () => ({
      descricao: aplicada,
      componentes: ehConector && componenteDoConector.length > 0 ? componenteDoConector.join(",") : undefined,
    }),
    [aplicada, ehConector, componenteDoConector],
  )

  const { data, isPending, error } = usarLista(esquema.rota, id, filtro, pagina)
  const salvar = usarSalvar(esquema.rota, id)
  const excluir = usarExcluir(esquema.rota, id)

  const itens = data?.itens ?? []
  const total = data?.total ?? 0
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA))
  const painelAberto = incluindo || selecionado != null

  const colunas: Coluna<ItemDeCadastro>[] = [
    // O conector só existe dentro de um componente, e "C413" sozinho não diz de
    // quem é — dois componentes podem ter conector de mesmo nome. Então o dono
    // vem primeiro, sempre, mesmo com o filtro de componente marcado: o filtro
    // fica numa barra acima e some da vista assim que a pessoa desce a lista.
    ...(ehConector
      ? [{
          chave: "componente",
          rotulo: "Componente",
          largura: 190,
          render: (x: ItemDeCadastro) => {
            const codigo = String(x.componenteCodigo ?? "").trim()
            const desc = String(x.componenteDescricao ?? "").trim()
            if (!codigo && !desc) {
              // Componente apagado deixa conector órfão no banco antigo.
              return <span style={{ color: "var(--erro)" }}>sem componente</span>
            }
            const repetida = desc.toLowerCase() === codigo.toLowerCase()
            return (
              <>
                {codigo && <span style={{ fontFamily: "var(--fm)", fontWeight: 600 }}>{codigo}</span>}
                {codigo && desc && !repetida && " "}
                {!repetida && desc}
              </>
            )
          },
        } as Coluna<ItemDeCadastro>]
      : []),
    ...esquema.colunas.map((c) => ({
      chave: c.nome, rotulo: c.rotulo, largura: c.largura, mono: c.mono,
      render: (x: ItemDeCadastro) => String(x[c.nome] ?? ""),
    })),
    { chave: "descricao", rotulo: "Descrição", minimo: 220, render: (x) => x.descricao },
    {
      chave: "qtd",
      rotulo: "Pinouts",
      largura: 86,
      mono: true,
      alinhamento: "direita",
      // O `qtd` vem pronto da API, de uma consulta agregada única. Nunca vale
      // uma segunda chamada por linha.
      render: (x) => (x.qtd > 0 ? x.qtd : <span style={{ color: "var(--txt3)" }}>—</span>),
    },
  ]

  async function gravar(dados: Record<string, unknown>): Promise<ResultadoDeSalvar> {
    try {
      const salvo = await salvar.mutateAsync({ id: selecionado?.id ?? null, dados })
      setIncluindo(false)
      setSelecionado(salvo)
      return { ok: true, id: salvo.id }
    } catch (e) {
      return { ok: false, erro: e }
    }
  }

  async function apagar() {
    if (!selecionado) return
    try {
      await excluir.mutateAsync(selecionado.id)
      setSelecionado(null)
    } catch {
      // O 409 de vínculo já aparece no painel; nada a fazer aqui.
    }
  }

  return (
    <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
      <div
        className="barra"
        style={{
          height: 38, flex: "0 0 38px", display: "flex", alignItems: "stretch", gap: 2,
          padding: "0 14px", background: "var(--surface2)", borderBottom: "1px solid var(--line2)",
          overflowX: "auto",
        }}
      >
        {ABAS.map((a) => (
          <button
            key={a}
            onClick={() => setAba(a)}
            aria-pressed={aba === a}
            style={{
              display: "flex", alignItems: "center", padding: "0 12px", fontSize: 12.5,
              fontWeight: aba === a ? 600 : 500,
              color: aba === a ? "var(--txt)" : "var(--txt2)",
              boxShadow: aba === a ? "inset 0 -2px 0 var(--brand)" : "none",
            }}
          >
            {ESQUEMAS[a].rotulo}
          </button>
        ))}
      </div>

      <div
        className="barra"
        style={{
          height: 40, flex: "0 0 40px", display: "flex", alignItems: "center", gap: 8,
          padding: "0 18px", background: "var(--surface)", borderBottom: "1px solid var(--line)",
        }}
      >
        <span
          style={{
            display: "flex", alignItems: "center", gap: 7, height: 28, padding: "0 9px",
            borderRadius: 7, background: "var(--surface2)", border: "1px solid var(--line2)",
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"
               style={{ flex: "0 0 13px", fill: "none", stroke: "var(--txt3)", strokeWidth: 1.9, strokeLinecap: "round" }}>
            <circle cx="11" cy="11" r="7" /><path d="m20 20-3.7-3.7" />
          </svg>
          <input
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") { setAplicada(busca.trim()); setPagina(1) }
              if (e.key === "Escape") { setBusca(""); setAplicada(""); setPagina(1) }
            }}
            placeholder={`Buscar ${esquema.rotulo.toLowerCase()} e apertar Enter`}
            aria-label={`Buscar ${esquema.rotulo}`}
            style={{ width: 230, border: 0, outline: "none", background: "transparent", fontSize: 12.5 }}
          />
        </span>

        {ehConector && (
          <Multi
            rotulo="Componente"
            catalogo={componentes}
            escolhidos={componenteDoConector}
            aoMudar={(v) => { setComponenteDoConector(v); setPagina(1) }}
            largura={190}
          />
        )}

        <span style={{ marginLeft: "auto" }} />

        {/* O "Exportar componentes" do menu antigo, que lista os 64.621 do acervo
            numa pagina so. Aqui e do projeto, e o id vai na primeira coluna para a
            planilha voltar pela importacao em lote sem ninguem ter de monta-la. */}
        {esquema.chave === "componentes" && (
          <a
            href={`/api/componentes/exportar?projetoId=${id}`}
            title="Planilha dos componentes deste projeto. O id vai na primeira coluna, para voltar pela importação em lote."
            style={{
              height: 28, display: "inline-flex", alignItems: "center", padding: "0 10px",
              borderRadius: 7, border: "1px solid var(--line)", background: "var(--surface)",
              color: "var(--txt)", fontSize: 12.5, textDecoration: "none",
            }}
          >
            Exportar
          </a>
        )}

        <button
          onClick={() => { setSelecionado(null); setIncluindo(true) }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6, height: 28, padding: "0 11px",
            borderRadius: 7, background: "var(--brand)", color: "#FFFFFF",
            fontSize: 12.5, fontWeight: 600,
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" aria-hidden="true"
               style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.4, strokeLinecap: "round" }}>
            <path d="M12 5.5v13M5.5 12h13" />
          </svg>
          Nov{esquema.artigo === "a" ? "a" : "o"} {esquema.singular}
        </button>
      </div>

      <div style={{ flex: 1, minHeight: 0, display: "flex" }}>
        <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
          <Tabela<ItemDeCadastro>
            colunas={colunas}
            itens={itens}
            chaveDe={(x) => x.id}
            selecionada={selecionado?.id ?? null}
            aoSelecionar={(x) => { setIncluindo(false); setSelecionado(x) }}
            vazio={
              isPending ? (
                <LinhaDeAviso>Carregando…</LinhaDeAviso>
              ) : error ? (
                <LinhaDeAviso>
                  <b style={{ color: "var(--erro)" }}>
                    {error instanceof ErroDaApi ? error.message : "Não foi possível falar com o servidor."}
                  </b>
                </LinhaDeAviso>
              ) : aplicada ? (
                <LinhaDeAviso>Nada encontrado com “{aplicada}”.</LinhaDeAviso>
              ) : (
                <LinhaDeAviso>Nenhum registro nesta aba.</LinhaDeAviso>
              )
            }
          />
        </div>

        {painelAberto && (
          <aside
            className="gaveta"
            style={{
              flex: "0 0 400px", width: 400, borderLeft: "1px solid var(--line2)",
              background: "var(--surface)", display: "flex", flexDirection: "column", minHeight: 0,
            }}
          >
            <PainelDeCadastro
              esquema={esquema}
              projetoId={id}
              acima={
                ehConector && selecionado && !incluindo
                  ? `Componente ${String(selecionado.componenteCodigo ?? selecionado.componenteDescricao ?? "—")}`
                  : undefined
              }
              registro={incluindo ? null : (selecionado as unknown as Record<string, unknown>)}
              salvando={salvar.isPending}
              aoSalvar={gravar}
              aoCancelar={() => { setIncluindo(false); setSelecionado(null) }}
              aoExcluir={apagar}
              excluindo={excluir.isPending}
              aoTransferir={selecionado ? () => setTransferindo(true) : undefined}
            />

            {transferindo && selecionado && (
              <div style={{ padding: "0 14px 14px" }}>
                <Transferir
                  rota={esquema.rota}
                  projetoId={id}
                  origem={selecionado}
                  candidatos={catalogoDaAba.itens}
                  aoFechar={() => setTransferindo(false)}
                  aoTransferir={() => { setTransferindo(false); setSelecionado(null) }}
                />
              </div>
            )}
          </aside>
        )}
      </div>

      <BarraDeEstado
        direita={
          paginas > 1 ? (
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <Passo ligado={pagina > 1} rotulo="Página anterior" aoIr={() => setPagina(pagina - 1)} seta="m14.5 6-6 6 6 6" />
              <span className="num" style={{ minWidth: 62, textAlign: "center" }}>{pagina} de {paginas}</span>
              <Passo ligado={pagina < paginas} rotulo="Próxima página" aoIr={() => setPagina(pagina + 1)} seta="m9.5 6 6 6-6 6" />
            </span>
          ) : null
        }
      >
        {total > 0 && (
          <span>
            <b className="num" style={{ color: "var(--txt)" }}>{total.toLocaleString("pt-BR")}</b>{" "}
            {esquema.rotulo.toLowerCase()}
          </span>
        )}
        <span style={{ color: "var(--txt3)" }}>Enter numa linha abre para editar · Esc fecha o painel</span>
      </BarraDeEstado>
    </div>
  )
}

function Passo({ ligado, rotulo, aoIr, seta }: {
  ligado: boolean; rotulo: string; aoIr: () => void; seta: string
}) {
  return (
    <button
      disabled={!ligado}
      onClick={aoIr}
      aria-label={rotulo}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 22, height: 22, borderRadius: 5, border: "1px solid var(--line2)",
        background: "var(--surface)", color: "var(--txt2)",
        opacity: ligado ? 1 : 0.4, cursor: ligado ? "pointer" : "default",
      }}
    >
      <svg width="12" height="12" viewBox="0 0 24 24" aria-hidden="true"
           style={{ fill: "none", stroke: "currentColor", strokeWidth: 2.2, strokeLinecap: "round", strokeLinejoin: "round" }}>
        <path d={seta} />
      </svg>
    </button>
  )
}
